import { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Crown, Globe } from "lucide-react";
import { Button } from "./ui/button";
import { AppContext } from "../App";
import { useLanguage, LANGUAGES } from "../contexts/LanguageContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_239eea52-71d9-4bb0-90f4-8c1a0c67cad2/artifacts/fi6bigz4_monacologo.png";

const Navbar = () => {
  const { settings } = useContext(AppContext);
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const location = useLocation();

  // Check if Girls section is redirecting to external link
  const girlsMode = settings?.girls_display_mode || (settings?.girls_section_enabled === false ? "external" : "internal");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  // Girls link always goes to /girls page (which shows redirect page when disabled)
  const navLinks = [
    { path: "/", label: language === "de" ? "Home" : "Home" },
    { path: "/girls", label: "Girls" },
    { path: "/bar", label: "Bar" },
    { path: "/separee", label: "Separee" },
    { path: "/polterabend", label: "Polterabend" },
    { path: "/gallery", label: language === "de" ? "Galerie" : "Gallery" },
    { path: "/anfahrtsplan", label: language === "de" ? "Anfahrt" : "Directions" },
    { path: "/jobs", label: "Jobs" },
    { path: "/newsletter", label: "Newsletter" },
    { path: "/contact", label: language === "de" ? "Kontakt" : "Contact" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-monaco-bg-0/95 backdrop-blur-lg border-b border-white/5" 
            : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center"
              data-testid="navbar-logo"
            >
              <img 
                src={LOGO_URL} 
                alt="Monaco Bar" 
                className="h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                    location.pathname === link.path
                      ? "text-monaco-red bg-monaco-red/10"
                      : "text-monaco-text-1 hover:text-monaco-text-0 hover:bg-white/5"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Language Switcher + CTA Button */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-monaco-text-1 hover:text-monaco-text-0 hover:bg-white/5 rounded-lg transition-colors"
                  data-testid="language-switcher"
                >
                  <Globe className="w-4 h-4" />
                  <span className="hidden sm:inline">{LANGUAGES[language].name}</span>
                  <span className="sm:hidden">{language.toUpperCase()}</span>
                </button>
                
                {isLangOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsLangOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-36 bg-monaco-bg-1 border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
                      {Object.values(LANGUAGES).map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setLanguage(lang.code);
                            setIsLangOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors ${
                            language === lang.code
                              ? "bg-monaco-red/10 text-monaco-red"
                              : "text-monaco-text-1 hover:bg-white/5 hover:text-monaco-text-0"
                          }`}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {settings?.booking_button_enabled !== false && (
                <Link to="/buchen" className="hidden sm:block">
                  <Button 
                    className="bg-monaco-red hover:bg-monaco-red-hover text-white px-6 btn-shine relative overflow-hidden neon-glow-hover"
                    data-testid="navbar-book-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    {language === "de" ? "Jetzt Buchen" : "Book Now"}
                  </Button>
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                className="lg:hidden p-2 text-monaco-text-0 hover:bg-white/5 rounded-lg"
                data-testid="mobile-menu-toggle"
              >
                {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div 
          className="absolute inset-0 bg-monaco-bg-0/95 backdrop-blur-lg"
          onClick={() => setIsMobileOpen(false)}
        />
        <nav className="absolute top-20 left-0 right-0 p-4 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-4 py-3 text-lg font-medium transition-colors rounded-lg ${
                location.pathname === link.path
                  ? "text-monaco-red bg-monaco-red/10"
                  : "text-monaco-text-1 hover:text-monaco-text-0 hover:bg-white/5"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/buchen" className="mt-4">
            <Button className="w-full bg-monaco-red hover:bg-monaco-red-hover text-white py-6 btn-shine relative overflow-hidden">
              <Crown className="w-5 h-5 mr-2" />
              {language === "de" ? "Jetzt Buchen" : "Book Now"}
            </Button>
          </Link>
        </nav>
      </div>
    </>
  );
};

export default Navbar;
